import { DuviNicheSitePageLayout } from "@/components/duvi-niche-site-page-layout";
import { DuviNicheSiteHero } from "@/components/duvi-niche-site-hero";
import { DuviNicheSiteTrustStrip } from "@/components/duvi-niche-site-trust-strip";
import { DuviNicheSiteFeatureCard } from "@/components/duvi-niche-site-feature-card";
import { DuviNicheSiteCtaBand } from "@/components/duvi-niche-site-cta-band";
import heroPhotograph from "@/assets/duvi-niche-site-hero.webp";
import type { DuviNicheSiteHomeViewProps } from "./duvi-niche-site-home.types";

/**
 * DuviNicheSiteHomeView — the apex landing page, composed from the bespoke
 * Fishwalkers design system exactly as the brochure prototype's `#home`
 * artboard composes it (duvi-design/exports/niches/fishwalkers/brochure.zip):
 * hero, trust strip, feature grid, closing CTA band, footer.
 *
 * Pure function of props, so a redraw from a future export touches only this
 * file.
 */
export function DuviNicheSiteHomeView({
  registerUrl,
  features,
}: DuviNicheSiteHomeViewProps) {
  return (
    <DuviNicheSitePageLayout registerUrl={registerUrl}>
      <DuviNicheSiteHero
        eyebrow="For fish walkers"
        headline="Get more fish walks booked"
        subheading="Fishwalkers gives your round its own page, so local fish owners can find you, see your walks, and book with confidence."
        primaryCta={{ label: "Sign up free", href: registerUrl }}
        secondaryCta={{ label: "See pricing", to: "/pricing" }}
        mediaSrc={heroPhotograph}
        mediaAlt="A fish walker on a suburban pavement, carrying a covered tank in a wheeled carrier"
      />

      <DuviNicheSiteTrustStrip line="Join the walkers already filling their rounds on Fishwalkers" />

      <section className="w-full bg-[var(--canvas)] py-[var(--section-y)] lg:py-[var(--section-y-lg)]">
        <div className="mx-auto flex w-full max-w-[var(--container-max)] flex-col gap-[var(--space-10)] px-[var(--container-pad)] lg:px-[var(--container-pad-lg)]">
          <h2 className="m-0 max-w-[28ch] font-[family-name:var(--font-display)] text-[length:var(--text-h2-sm)] font-extrabold leading-[var(--leading-heading)] tracking-[var(--tracking-display)] text-[color:var(--heading)] text-pretty lg:text-[length:var(--text-h2)]">
            Set up your round in minutes
          </h2>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-[var(--grid-gap)] lg:gap-[var(--grid-gap-lg)]">
            {features.map((feature) => (
              <DuviNicheSiteFeatureCard
                key={feature.title}
                icon={feature.icon}
                title={feature.title}
                body={feature.body}
              />
            ))}
          </div>
        </div>
      </section>

      <DuviNicheSiteCtaBand
        heading="Ready to fill your round?"
        subheading="Create your walker page in a few minutes — no payment required to get started."
        ctaLabel="Sign up free"
        ctaHref={registerUrl}
        tone="soft"
      />
    </DuviNicheSitePageLayout>
  );
}
